import { observable, action, computed, makeAutoObservable } from 'mobx';
import moment from 'moment';
import { SdkStaff } from 'src/AppState/sdk/staff';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { FormModel, Result } from 'src_common/common/mobx-utils/Form2/FormModel';
import { validateNotEmpty } from 'src_common/common/mobx-utils/Form2/validators';
import { getTranslationFor } from 'src/locale/lang';
import { Amount } from 'src_common/common/amount/Amount';
import { ReduxAction } from 'src/AppState/ReduxAction';
import { amountViewToValue } from 'src/components/CustomersBar/CustomerTab/BankingTab/components/amountViewToValue';
import { Common } from 'src/AppState/Common';
import { ConfigState } from 'src/AppState/ConfigState/ConfigState';
import { DateTime } from 'src_common/utils/time/time';

export interface DepositHistoryPeriodType {
    startDate: string;
    endDate: string;
}

const DateFormat = 'YYYY-MM-DD';
const noEmptyMessage = 'This field is Required';

interface FormType {
    amount: string;
    promotionId: string;
    expiryDate: string;
}

export class FreeBetsFormState {
    public readonly amount: FormInputState<string, Amount>;
    public readonly promotionId: FormInputState<string, string>;
    public readonly expiryDate: FormInputState<string, string>;

    @observable public successMessage: string | null = null;
    @observable public errorMessage: string | null = null;

    private readonly form: FormModel<FormType>;

    public constructor(
        private readonly sdkStaff: SdkStaff,
        private readonly permisionsAllowed: boolean,
        private readonly customer_id: number,
        private readonly reduxAction: ReduxAction,
        private readonly submitCallback: () => void,
        private readonly common: Common
    ) {
        this.amount = FormInputState.new('').map((newAmount) => {
            if (newAmount === '') {
                return Result.createError('Incorrect number');
            }

            try {
                const convertedNewAmount = new Amount(newAmount);

                const splited = convertedNewAmount.value.split('.')[1];

                const haveMoreThan2DecimalPlaces = splited !== undefined && splited.length > 2;

                if (
                    convertedNewAmount.isLessThanZero() ||
                    convertedNewAmount.isEqualWith(new Amount('0')) ||
                    haveMoreThan2DecimalPlaces === true
                ) {
                    return Result.createError('Incorrect number');
                }

                return Result.createOk(convertedNewAmount);
            } catch (error) {
                return Result.createError('Incorrect number');
            }
        });

        this.promotionId = FormInputState.new('').map(validateNotEmpty(noEmptyMessage));

        this.expiryDate = FormInputState.new('')
            .map(validateNotEmpty(noEmptyMessage))
            .map((newDate) => {
                const convertedNewDate = moment(newDate, DateFormat);

                if (convertedNewDate.isValid() === false) {
                    return Result.createError(getTranslationFor('ERROR_INVALID_DATE'));
                }

                if (moment().diff(convertedNewDate) > 0) {
                    return Result.createError(getTranslationFor('ERROR_INVALID_DATE'));
                }

                return Result.createOk(convertedNewDate.toISOString());
            });

        this.form = FormModel.group({
            amount: this.amount.map((amount) => Result.createOk(amount.value)),
            promotionId: this.promotionId,
            expiryDate: this.expiryDate,
        });
        makeAutoObservable(this);
    }

    @computed public get currAmount(): Amount {
        try {
            return this.amount.result.value.type === 'ok' ? this.amount.result.value.data : new Amount('0');
        } catch (error) {
            console.error(error);
            return new Amount('0');
        }
    }

    public getNewBalance(freebetCreditsTotalAmount: Amount): Amount {
        try {
            return freebetCreditsTotalAmount.add(this.currAmount);
        } catch (error) {
            console.error(error);
            return freebetCreditsTotalAmount;
        }
    }

    public onChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const inputValue = e.target.value;
        const formatValue = amountViewToValue(inputValue);
        this.amount.setValue(formatValue);
    };

    @action public handleSetAmount = (newAmount: Amount): void => {
        this.amount.setValue(newAmount.value);
    };

    @action public handleResetAmount = (): void => {
        this.amount.reset();
    };

    @action public handleSetPromotionId = (newPromotionId: string): void => {
        this.promotionId.setValue(newPromotionId);
    };

    @action public handleCalendarChange = (newDate: string): void => {
        this.expiryDate.setValue(newDate);
    };

    @action public handleCalendarReset = (): void => {
        this.expiryDate.reset();
    };

    @action public handleSubmit = async (): Promise<void> => {
        this.form.setAsVisited();
        this.successMessage = null;
        this.errorMessage = null;

        try {
            if (this.form.result.value.type === 'error' || this.permisionsAllowed === false) {
                return;
            }

            const convertedAmount = $appState.configState.precision.valueOldFormat(new Amount(this.amount.value));

            const isBonusEngineEnabled = ConfigState.get(this.common).config.showBonusEngine;
            const userId = this.customer_id.toString(10);
            if (isBonusEngineEnabled) {
                const response = await this.common.trpcClient.client.bonusCampaignsRouter.addFreebet.mutate({
                    [userId]: {
                        'free-bet': {
                            amount: convertedAmount,
                            platform: 'sports-book',
                            source: 'manual',
                            expiryAt: DateTime.from(this.expiryDate.value)?.utc().format('YYYY-MM-DDTHH:mm:ss') ?? '',
                            usageConditions: [],
                            rewardName: this.promotionId.value,
                        },
                    },
                });

                if (response.status !== 200) {
                    this.errorMessage = this.mapErrorMessage(response.body.message);
                    return;
                }
            } else {
                const response = await this.sdkStaff.postAddFreebetCredit({
                    customer_id: this.customer_id,
                    requestBody: {
                        amount: convertedAmount,
                        promotionId: this.promotionId.value,
                        enabled: true,
                        expiryDate: this.expiryDate.value,
                    },
                });

                if (response.status === 'error') {
                    this.errorMessage = this.mapErrorMessage(response.message);
                    return;
                }
            }

            this.submitCallback();
            this.form.reset();

            this.successMessage = 'Account has been adjusted successfully';
            this.reduxAction.refreshFreebets(this.customer_id);
        } catch (error) {
            this.errorMessage = this.mapErrorMessage('unkown');
            console.error(error);
        }
    };

    public mapErrorMessage = (message: string): string => {
        if (message.includes('bonus-credit-unique-promotionId-violation')) {
            return 'There is already a campaign with this name';
        }

        return 'UnkownError';
    };
}
